/**
 * This module manages the notifications model of the API
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */
import Vue from 'vue'
import {
	VUEX_NOTIFICATIONS_ADD_JOB_NOTIFICATION,
	VUEX_NOTIFICATIONS_CLEAR,
	VUEX_NOTIFICATIONS_DELETE_REQUEST,
	VUEX_NOTIFICATIONS_DELETE_REQUEST_FAILURE,
	VUEX_NOTIFICATIONS_DELETE_REQUEST_SUCCESS,
	VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST,
	VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST_FAILURE,
	VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST_SUCCESS,
	VUEX_NOTIFICATIONS_INIT,
	VUEX_NOTIFICATIONS_NEW_NOTIFICATION,
	VUEX_NOTIFICATIONS_NEXT_PAGE_REQUEST,
	VUEX_NOTIFICATIONS_READ_REQUEST,
	VUEX_NOTIFICATIONS_READ_REQUEST_FAILURE,
	VUEX_NOTIFICATIONS_READ_REQUEST_SUCCESS,
	VUEX_NOTIFICATIONS_REQUEST,
	VUEX_NOTIFICATIONS_REQUEST_FAILURE,
	VUEX_NOTIFICATIONS_REQUEST_SUCCESS,
	VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST,
	VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST_FAILURE,
	VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST_SUCCESS,
	VUEX_NOTIFICATIONS_SYNC_PAGINATION,
	VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST,
	VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST_FAILURE,
	VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST_SUCCESS,
	VUEX_NOTIFICATIONS_SET_TYPE,
	VUEX_NOTIFICATIONS_TYPE_DEPARTMENT,
	VUEX_NOTIFICATIONS_TYPE_JOB,
} from '@/store/constants/notifications'

import { VUEX_AUTH_LOGOUT } from '@/store/constants/auth'
import api from '@/api'
import dayjs from 'dayjs'

const paginationDefault = {
  lastPage: 1,
	page: 1,
 	rowsPerPage: 50,
	totalItems: 0,
}

const state = {
	notifications: [],
	isLoading: false,
	lastNotificationTime: null,
	notificationsType: 'job',
	unseenCount: null,
	preferences: {},
	pagination:  {
	  lastPage: 1,
		page: 1,
	 	rowsPerPage: 50,
		totalItems: 0,
	},
}

const getters = {}

const actions = {
	[VUEX_NOTIFICATIONS_INIT]: async ({ dispatch }) => {
		await dispatch(VUEX_NOTIFICATIONS_REQUEST)
	},

	[VUEX_NOTIFICATIONS_REQUEST]: async ({ state, commit }, extraParams) => {

		commit(VUEX_NOTIFICATIONS_REQUEST)

		let params = Object.assign({
			page: state.pagination.page,
			per_page: state.pagination.rowsPerPage,
		}, extraParams)

    	const apiUrl = `notifications/?appDomain=${state.notificationsType}&include=avatar&sort=-created_at`

		await api.get(apiUrl, { params }).then(async (response) => {
			commit(VUEX_NOTIFICATIONS_REQUEST_SUCCESS, {
				data: response.data,
				persistNotifications: params.persistNotifications
			})
		}).catch((err) => {
			commit(VUEX_NOTIFICATIONS_REQUEST_FAILURE, err)
		})
	},

	[VUEX_NOTIFICATIONS_NEXT_PAGE_REQUEST]: async ({ commit, dispatch }, extraParams) => {
		if (state.pagination.page === state.pagination.lastPage) {
			return
		}

		commit(VUEX_NOTIFICATIONS_NEXT_PAGE_REQUEST)

		await dispatch(VUEX_NOTIFICATIONS_REQUEST, {
			...extraParams,
			persistNotifications: true
		})
	},

	[VUEX_NOTIFICATIONS_DELETE_REQUEST]: async ({ state, commit, dispatch },
		{ id, deleteAll = false }) => {
		commit(VUEX_NOTIFICATIONS_DELETE_REQUEST)

		let endpoint = ''
		let payload

		if (deleteAll) {
			if (state.notifications.length === 0)
				return

			payload = { data : { all: true, app_domain: state.notificationsType } }
		} else if(id) {
			endpoint = `/${id}`
		} else {
			commit(VUEX_NOTIFICATIONS_DELETE_REQUEST_FAILURE, 'Wrong params provided')
			return
		}

		await api.delete(`notifications${endpoint}`, payload).then((response) => {
			commit(VUEX_NOTIFICATIONS_DELETE_REQUEST_SUCCESS)
			dispatch(VUEX_NOTIFICATIONS_REQUEST)
		}).catch((err) => {
			commit(VUEX_NOTIFICATIONS_DELETE_REQUEST_FAILURE, err)
		})
	},

	[VUEX_NOTIFICATIONS_READ_REQUEST]: async ({state, commit, dispatch },
		{ id, readAt = dayjs().format('YYYY-MM-DD HH:mm:ss'), readAll = false }) => {
		commit(VUEX_NOTIFICATIONS_READ_REQUEST)

		let endpoint = ''
		let payload

		if (readAll) {
			if (state.notifications.length === 0)
				return

			payload = { read_at: readAt, all: true, app_domain: state.notificationsType }
			endpoint = '/read'
		} else if(id) {
			endpoint = `/${id}`
			payload = { read_at: readAt }
		} else {
			commit(VUEX_NOTIFICATIONS_READ_REQUEST_FAILURE, 'Wrong params provided')
			return
		}

		await api.patch(`notifications${endpoint}`, payload).then((response) => {
			commit(VUEX_NOTIFICATIONS_READ_REQUEST_SUCCESS)
			dispatch(VUEX_NOTIFICATIONS_REQUEST)
		}).catch((err) => {
			commit(VUEX_NOTIFICATIONS_READ_REQUEST_FAILURE, err)
		})
	},

	[VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST]: async ({ state, commit },) => {
		commit(VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST)

		await api.get('notification_preferences').then((response) => {
			commit(VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST_SUCCESS, response.data.data)
		}).catch((err) => {
			commit(VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST_FAILURE, err)
		})
	},

	[VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST]: async ({ state, commit }, payload) => {
		commit(VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST)

		await api.patch('notification_preferences', payload).then((response) => {
			commit(VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST_SUCCESS)
		}).catch((err) => {
			commit(VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST_FAILURE, err)
		})
	},

	[VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST]: async (
		{ state, commit, rootState },
		seenAt = dayjs().format('YYYY-MM-DD HH:mm:ss')
	) => {
		commit(VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST)

		await api.patch(`users/${rootState.auth.currentUser.id}`, { notifications_seen_at: seenAt })
		.then((response) => {
			commit(VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST_SUCCESS)
		}).catch((err) => {
			commit(VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST_FAILURE, err)
		})

        //APPCUES
        window.Appcues.track("Open Notification Preferences");
	},

	[VUEX_NOTIFICATIONS_NEW_NOTIFICATION]: async ({ commit, state, rootState }, payload) => {
		// only need to fetch right now if tray is open
		if (rootState.ui.leftDrawer.openState && payload.app_domain === state.notificationsType) {
			await api.get(`notifications/${payload.notification_id}?include=avatar`)
			.then((response) => {
				commit(VUEX_NOTIFICATIONS_NEW_NOTIFICATION, response.data.data)
			}).catch((err) => {
				console.error('Notification fetch err',err)
				//commit(VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST_FAILURE, err)
			})
		}
		else commit(VUEX_NOTIFICATIONS_NEW_NOTIFICATION, null)
	},

	[VUEX_NOTIFICATIONS_CLEAR]: ({ commit }) => {
		commit(VUEX_NOTIFICATIONS_CLEAR_NOTIFICATIONS)
	},

	[VUEX_NOTIFICATIONS_SYNC_PAGINATION]: ({ commit }) => {
		commit(VUEX_NOTIFICATIONS_SYNC_PAGINATION)
	},

	[VUEX_NOTIFICATIONS_SET_TYPE]: async ({ commit, dispatch }, notificationsType=VUEX_NOTIFICATIONS_TYPE_JOB) => {
		commit(VUEX_NOTIFICATIONS_SET_TYPE, notificationsType)
		commit(VUEX_NOTIFICATIONS_SYNC_PAGINATION)
		commit(VUEX_NOTIFICATIONS_CLEAR)

		await dispatch(VUEX_NOTIFICATIONS_REQUEST)
	},

}

const mutations = {

	[VUEX_NOTIFICATIONS_DELETE_REQUEST]: (state) => {},

	[VUEX_NOTIFICATIONS_DELETE_REQUEST_FAILURE]: (state) => {},

	[VUEX_NOTIFICATIONS_READ_REQUEST]: (state) => {},

	[VUEX_NOTIFICATIONS_READ_REQUEST_FAILURE]: (state) => {},

	[VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST]: (state) => {},

	[VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST_FAILURE]: (state) => {},

	[VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST]: (state) => {},

	[VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST_SUCCESS]: (state) => {},

	[VUEX_NOTIFICATIONS_SET_PREFERENCES_REQUEST_FAILURE]: (state) => {},

	[VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST]: (state) => {},

	[VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST_SUCCESS]: (state) => {},

	[VUEX_NOTIFICATIONS_VIEWED_AT_REQUEST_FAILURE]: (state) => {},

	[VUEX_NOTIFICATIONS_DELETE_REQUEST_SUCCESS]: (state) => {},

	[VUEX_NOTIFICATIONS_READ_REQUEST_SUCCESS]: (state) => {},

	[VUEX_NOTIFICATIONS_REQUEST]: (state) => {
		state.isLoading = true
	},

	[VUEX_NOTIFICATIONS_REQUEST_FAILURE]: (state) => {
		state.isLoading = false
	},

	[VUEX_NOTIFICATIONS_REQUEST_SUCCESS]: (state, { data, persistNotifications }) => {
		let notifications = data.data.map(item => {
			item.data.read_at = item.read_at
			item.data.id = item.id
			item.data.notificationType = item.type

			return item.data
		})

		if (persistNotifications) {
			Vue.set(state, 'notifications', state.notifications.concat(notifications))
		} else {
			Vue.set(state, 'notifications', notifications)
		}

		state.pagination = Object.assign({}, paginationDefault)
		state.pagination.totalItems = data.meta.total
    	state.pagination.page = data.meta.current_page
    	state.pagination.lastPage = data.meta.last_page
		state.unseenCount = data.meta.unseen_count
		state.lastUpdateTime = Date.now()
		state.isLoading = false
	},

	[VUEX_NOTIFICATIONS_SYNC_PAGINATION]: (state, pagination) => {
		state.pagination = Object.assign({}, paginationDefault, pagination)
	},

	[VUEX_NOTIFICATIONS_GET_PREFERENCES_REQUEST_SUCCESS]: (state, payload) => {
		Vue.set(state, 'preferences', payload)
	},

	[VUEX_NOTIFICATIONS_SET_TYPE]: (state, notificationsType) => {
		state.notificationsType = notificationsType
	},

	[VUEX_NOTIFICATIONS_NEW_NOTIFICATION]: (state, payload) => {
		if (payload && payload.app_domain === state.notificationsType && !state.notifications.find(n => n.id === payload.id)) {
			payload.read_at = null
			payload.notificationType = payload.type
			state.notifications.unshift(payload)
		}

		state.lastUpdateTime = Date.now()
		state.unseenCount = state.unseenCount + 1
	},

	[VUEX_NOTIFICATIONS_NEXT_PAGE_REQUEST]: (state) => {
		state.pagination.page = state.pagination.page + 1
	},

	[VUEX_NOTIFICATIONS_CLEAR]: (state, payload) => {
		Vue.set(state, 'notifications', [])
		state.lastUpdateTime = null
	},

	[VUEX_AUTH_LOGOUT]: (state) => {
		Vue.set(state, 'notifications', [])
		state.lastUpdateTime = null
	},

}

/**
 * The Notifications module of our Vuex store.
 * @module store/modules/notifications
 */
export default {
  state,
  getters,
  actions,
  mutations,
}